import { ApolloClient } from "@apollo/client";
import { isEqual } from "lodash";
import { AgGrid } from "@jsq/ds/core";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import { GetPaymentBatchesConnectionDocument } from "#graphql/supergraphHooks";
import {
  type GetPaymentBatchesConnectionQuery,
  type PaymentBatchesFilterInput,
} from "#graphql/supergraphTypes";
import { clientContext } from "#utils/graphql_utils";
import AmountRefPreserver from "./amountRefPreserver";
import { paymentBatchGQLFactory } from "./Factories/paymentBatchGQLFactory";
import { PaymentRowItem } from "./types";

/**
 * Provides the data source for the Payment Batches table.
 * The payment batches are fetched from the GraphQL API. AGGrid invokes the `getRows` method,
 * which fetches the data from the API and provides it to the grid.
 */
export class PaymentBatchesGraphqlDataSource implements AgGrid.IServerSideDatasource {
  private apiClient: ApolloClient<object>;
  private amountRefPreserver: AmountRefPreserver;
  private refInputFilter: PaymentBatchesFilterInput = {};
  private isGPX: boolean;
  private isM3Enabled: boolean;

  constructor(
    client: ApolloClient<object>,
    amountRefPreserver: AmountRefPreserver,
    isGPX: boolean,
    isM3Enabled: boolean = false,
  ) {
    this.apiClient = client;
    this.amountRefPreserver = amountRefPreserver;
    this.isGPX = isGPX;
    this.isM3Enabled = isM3Enabled;
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams<PaymentRowItem>): void {
    const { api, request } = params;
    const { filterModel, sortModel, startRow } = request;
    api.showLoadingOverlay();
    const inputFilter = paymentBatchGQLFactory.toInputFilterVariables(
      filterModel,
      sortModel,
      this.amountRefPreserver.amountRef,
      this.isGPX,
      this.isM3Enabled,
    );

    // Reset the pagination if the filter has changed
    const pageIndex = isEqual(this.refInputFilter, inputFilter) ? startRow : 0;
    const paginationArgs = paymentBatchGQLFactory.toPaginationVariables(pageIndex);

    this.refInputFilter = inputFilter;
    this.apiClient
      .query<GetPaymentBatchesConnectionQuery>({
        query: GetPaymentBatchesConnectionDocument,
        context: clientContext(),
        variables: {
          inputFilter: inputFilter,
          paginationArgs: paginationArgs,
          isGPX: this.isGPX,
        },
        fetchPolicy: "no-cache",
      })
      .then((response) => {
        const successParams = paymentBatchGQLFactory.toLoadSuccessParams(
          response.data,
          this.isGPX,
        );
        if (successParams.amountRef)
          this.amountRefPreserver.amountRef = successParams.amountRef;
        if (successParams.resp.rowCount === 0) {
          api.showNoRowsOverlay();
        } else {
          api.hideOverlay();
        }
        params.success(successParams.resp);
      })
      .catch((e: Error) => {
        addError(formatError(e));
        api.hideOverlay();
        params.fail();
      });
  }
}
